// Documentation content for AI Joomla Mobile Menu.
// Rewritten from the vendor's public docs (Menu Content Types, Navigation
// Types, Menu Positioning, Getting Started) in our own voice.

export const mobileMenuDocs = {
  slug: 'ai-joomla-mobile-menu',
  name: 'AI Joomla Mobile Menu',
  tagline: 'Responsive vertical & mobile menu module for Joomla 5 & 6',
  accent: 'teal',
  sections: [
    {
      id: 'requirements',
      title: 'Requirements',
      body: [
        'AI Joomla Mobile Menu runs on any server where Joomla itself runs without issues — there are no unusual server requirements.',
        'Supported: Joomla 5.x and 6.x, PHP 8.1 through 8.5. We recommend keeping Joomla and PHP on a currently-supported version for the best compatibility and security.',
      ],
    },
    {
      id: 'installation',
      title: 'Installation',
      body: [
        'Download the package ZIP from your AI Joomla account after purchase.',
        'Log in to your Joomla administrator panel with a Super User account.',
        'Go to System → Manage → Install, click Upload & Install, and select the downloaded ZIP.',
        'Joomla will install three components together: the Mobile Menu module itself, a small Dojo loader plugin, and a params plugin used by the module\u2019s admin form fields. All three finishing with a green "successfully installed" message means you\u2019re ready to configure it.',
        'If you see an error during install, double-check you\u2019re on a supported PHP version, then retry the upload.',
      ],
    },
    {
      id: 'quick-start',
      title: 'Quick start',
      body: [
        'Go to Content → Site Modules → New, and choose "AI Joomla Mobile Menu" from the module type list.',
        'Under Options, pick a Menu Content Type (which content the menu is built from — see below), a Navigation Type (how submenus behave) and a Positioning mode (where/how the menu appears on screen).',
        'Set Status to Published, choose a module Position (or use Popup/Sidebar/Overlay positioning instead — these don\u2019t require a traditional template position), and assign it to the pages you want under Menu Assignment.',
        'Save & Close, then view your site — the menu is live.',
      ],
    },
    {
      id: 'content-types',
      title: 'Menu content types',
      body: [
        'The Content Type setting decides what the menu is built from:',
        '• Joomla menu type — mirrors one of your existing Joomla menus.',
        '• Joomla content type — builds the menu straight from your content categories & articles.',
        '• VirtueMart — shown categories pulled live from VirtueMart 2.x/3.x, with product counts.',
        '• K2 — same idea, sourced from K2 categories.',
        '• ZOO — choose a ZOO app, then which categories from it to display.',
        '• JoomShopping, HikaShop, RedShop, MijoShop — each has its own native category source, so your store navigation always matches your live catalog without manual upkeep.',
        'Whichever type you pick, new categories/articles/products you add later show up automatically — there\u2019s nothing to re-sync.',
      ],
    },
    {
      id: 'navigation-types',
      title: 'Navigation types',
      body: [
        'Navigation Type controls how a parent item reveals its children:',
        '• Slide — the classic smartphone-style menu: tapping a parent slides its submenu in to replace the current level, with a Back item to return.',
        '• Drop-down — children appear next to their parent instead of replacing the view, closer to a desktop-style menu.',
        '• Tree — children appear directly beneath their parent in an indented, expandable hierarchy, like a file browser.',
        '• Expanded — every level is visible at once, no click/tap needed to reveal anything — good for menus that are always meant to act like a full sitemap.',
        '• Accordion — each submenu can be expanded or collapsed independently, and multiple branches can stay open at the same time.',
      ],
    },
    {
      id: 'positioning',
      title: 'Menu positioning modes',
      body: [
        'Positioning decides how the menu is placed on the page, independent of the navigation type above:',
        '• Module Position — the standard Joomla way: the menu renders inline wherever you publish the module position, like any other module.',
        '• Popup — the menu opens in an overlay panel triggered by a click, and can even be triggered from the corner of the page rather than a fixed module position.',
        '• Sidebar — the menu slides in from a chosen edge of the screen with GPU-accelerated animation. There are 14 built-in slide animations to choose from.',
        '• Overlay — a full-screen navigation take-over, hiding the traditional menu entirely in favor of a modern, immersive nav pattern.',
      ],
    },
    {
      id: 'other-features',
      title: 'Other features',
      body: [
        'Icons — attach a Font Awesome icon (or a custom uploaded image) to any menu item.',
        'Live filter — an optional search box lets visitors type to instantly filter long menus down to matching items.',
        'Module positions inside the menu — you can drop any other Joomla module (a mini search box, social icons, a language switcher) into the menu\u2019s header, footer, or a specific submenu level.',
        'Smooth-scroll navigation — for one-page sites, menu items can smooth-scroll to a section on the same page and automatically highlight the active section as visitors scroll.',
      ],
    },
    {
      id: 'faq',
      title: 'FAQ & troubleshooting',
      body: [
        'Q: Can I run more than one instance on the same site? A: Yes — publish the module multiple times with different Menu Assignment rules (e.g. a Sidebar menu everywhere plus a Popup menu only on the homepage).',
        'Q: Does this replace my main Joomla menu? A: No — it\u2019s an additional module. Your existing menu, template and other modules are untouched; you choose where this one is published.',
        'Q: My VirtueMart/HikaShop categories aren\u2019t showing up. A: Confirm the store extension is installed and categories are published, then re-open the module\u2019s Options tab and re-select the content type — Joomla sometimes needs the field re-rendered after a store extension is installed later.',
        'Q: How do I upgrade to a new version? A: Simply install the newer ZIP over the top from System → Manage → Install, the same way as the first install — your saved module settings are preserved.',
      ],
    },
  ],
};
